import { Decimal, ZERO } from './money';

/**
 * Merchant feature (M1). Admin-defined registration requirement. The admin
 * configures which of these show on the public merchant registration form
 * (`tenant_config.features.merchant_requirements`); each submission lands in
 * `merchant_requirement_submissions` for review at approval.
 */
export type MerchantRequirementDef = {
  /** Stable slug stored on `merchant_requirement_submissions.requirement_key`. */
  key: string;
  label: string;
  /** Help text shown under the field on the registration form. */
  description?: string;
  /** When false, registration may proceed without it. */
  required: boolean;
  /** true = file upload (R2); false = free-text value. */
  acceptsFile: boolean;
  /** Allowed MIME types / extensions for file requirements (advisory hint). */
  fileTypes?: string[];
};

/** No requirements out of the box — the admin defines them in Settings. */
export const MERCHANT_REQUIREMENTS_DEFAULT: MerchantRequirementDef[] = [];

/**
 * Merchant payout cadence hint. Merchants cash out on their own schedule,
 * distinct from members; the admin batches merchant payout requests on this
 * rhythm. Informational/admin-facing — the batch flow itself is manual.
 */
export type MerchantPayoutSchedule = 'weekly' | 'biweekly' | 'monthly';

/**
 * Resolve the snapshot platform-fee % for a merchant product line. Clamped to
 * 0–100. The merchant earns `lineTotal × (1 − pct/100)`; the platform keeps
 * the remainder (no company wallet — the fee is simply not credited out, the
 * same accounting rationale as payout deductions).
 */
export function clampFeePct(value: Decimal | string | number | null | undefined): Decimal {
  if (value === null || value === undefined) return ZERO;
  const d = value instanceof Decimal ? value : new Decimal(value);
  if (d.lt(0)) return ZERO;
  if (d.gt(100)) return new Decimal(100);
  return d;
}

/** Merchant's cash earning for a line: `lineTotal × (1 − feePct/100)`. */
export function merchantEarning(lineTotal: Decimal, feePct: Decimal | string | number): Decimal {
  const pct = clampFeePct(feePct);
  return lineTotal.mul(new Decimal(100).minus(pct)).div(100);
}

/** Clamp any 0–100 percentage (alias of {@link clampFeePct} for readability). */
export const clampPct = clampFeePct;

/**
 * Clamp a merchant-declared platform fee % into the admin-set `[min, max]`
 * range (each already a valid 0–100 percentage; min/max are swapped if given
 * reversed). A merchant declares their own fee, but the platform constrains it
 * to this band. Returns the clamped Decimal.
 */
export function clampFeeToRange(
  value: Decimal | string | number | null | undefined,
  min: Decimal | string | number | null | undefined,
  max: Decimal | string | number | null | undefined,
): Decimal {
  const v = clampFeePct(value);
  let lo = clampFeePct(min);
  let hi = max === null || max === undefined ? new Decimal(100) : clampFeePct(max);
  if (lo.gt(hi)) [lo, hi] = [hi, lo];
  if (v.lt(lo)) return lo;
  if (v.gt(hi)) return hi;
  return v;
}

/** Platform commission for a line: `lineTotal × feePct/100`. */
export function merchantCommission(lineTotal: Decimal, feePct: Decimal | string | number): Decimal {
  return lineTotal.mul(clampFeePct(feePct)).div(100);
}

/**
 * PV derived from the commission: `commission × pvCommissionPct/100`. This is
 * the merchant pricing model's PV — NOT the flat `products.pv`. e.g. a 50%
 * pvCommissionPct on a ₱90 commission yields 45 PV.
 */
export function pvFromCommission(
  commission: Decimal,
  pvCommissionPct: Decimal | string | number | null | undefined,
): Decimal {
  return commission.mul(clampFeePct(pvCommissionPct)).div(100);
}

/**
 * Resolve the per-level CASH amounts for one RTU line (PER-UNIT — pass `qty` to
 * scale). The pool = `commission × ceilingPct/100`; each level then takes its own
 * share of the pool: `pool × share_k/100 × qty`. Index 0 = buyer L0, k = Lk
 * upline. Returns `[]` when ceiling/shares are empty or the pool is zero. This is
 * what gets snapshotted onto `order_items.rtu_level_amounts` at checkout.
 */
export function rtuLineAmounts(
  commission: Decimal,
  ceilingPct: Decimal | string | number | null | undefined,
  levelShares: Array<string | number | Decimal> | null | undefined,
  qty: number,
): Decimal[] {
  if (!levelShares || levelShares.length === 0) return [];
  const pool = commission.mul(clampPct(ceilingPct)).div(100);
  if (pool.lte(0)) return [];
  const mult = new Decimal(qty);
  return levelShares.map((share) => pool.mul(clampPct(share)).div(100).mul(mult));
}

/**
 * Apply VAT to the merchant's gross: `gross × (1 − vatPct/100)` when enabled,
 * else `gross` unchanged. e.g. ₱810 gross at 12% VAT → ₱712.80 net.
 */
export function applyVat(
  gross: Decimal,
  vatEnabled: boolean,
  vatPct: Decimal | string | number | null | undefined,
): Decimal {
  if (!vatEnabled) return gross;
  return gross.mul(new Decimal(100).minus(clampFeePct(vatPct))).div(100);
}

/** Resolved per-line merchant rates (a variant inherits NULLs from its product). */
export type MerchantLineRates = {
  platformFeePct: string | null;
  pvCommissionPct: string | null;
  vatEnabled: boolean;
  vatPct: string;
  // Realtime Unilevel: pool ceiling (% of commission) + per-level shares (% of
  // pool, index 0 = buyer L0). NULL ceiling / null shares = RTU not configured.
  rtuCeilingPct: string | null;
  rtuLevelShares: string[] | null;
};

/** Product-level rate carrier (the parent fallback for a variant). */
type ProductRateSource = {
  platformFeePct?: string | null;
  pvCommissionPct?: string | null;
  vatEnabled?: boolean | null;
  vatPct?: string | null;
  rtuCeilingPct?: string | null;
  rtuLevelShares?: string[] | null;
};

/** Variant-level rate carrier; any NULL field inherits the product value. */
type VariantRateSource = {
  platformFeePct?: string | null;
  pvCommissionPct?: string | null;
  vatEnabled?: boolean | null;
  vatPct?: string | null;
  rtuCeilingPct?: string | null;
  rtuLevelShares?: string[] | null;
};

/**
 * Resolve the effective fee/PV/VAT rates for a line, applying the
 * inherit-from-product fallback when a variant leaves a field NULL. Pass
 * `variant = null` for a single-SKU line (uses the product values directly).
 */
export function resolveVariantRates(
  product: ProductRateSource,
  variant?: VariantRateSource | null,
): MerchantLineRates {
  const pick = <T>(v: T | null | undefined, p: T | null | undefined): T | null =>
    v !== null && v !== undefined ? v : (p ?? null);
  return {
    platformFeePct: pick(variant?.platformFeePct, product.platformFeePct),
    pvCommissionPct: pick(variant?.pvCommissionPct, product.pvCommissionPct),
    vatEnabled: pick(variant?.vatEnabled, product.vatEnabled) ?? false,
    vatPct: pick(variant?.vatPct, product.vatPct) ?? '0',
    rtuCeilingPct: pick(variant?.rtuCeilingPct, product.rtuCeilingPct),
    rtuLevelShares: pick(variant?.rtuLevelShares, product.rtuLevelShares),
  };
}

/**
 * Compute the full money breakdown for one merchant order line given the line
 * total (unitPrice × qty) and the resolved rates. Single source of truth shared
 * by checkout (snapshot), the storefront preview, and the completion credit.
 */
export function resolveMerchantLine(
  lineTotal: Decimal,
  rates: MerchantLineRates,
): {
  lineTotal: Decimal;
  commission: Decimal;
  gross: Decimal;
  net: Decimal;
  pv: Decimal;
  rtuLevelAmounts: Decimal[];
} {
  const commission = merchantCommission(lineTotal, rates.platformFeePct ?? '0');
  const gross = lineTotal.minus(commission);
  const net = applyVat(gross, rates.vatEnabled, rates.vatPct);
  const pv = pvFromCommission(commission, rates.pvCommissionPct);
  // PER-UNIT semantics: callers pass the UNIT lineTotal (price × 1) so the
  // returned RTU amounts are per-unit, matching `unit_pv`. The comp plan scales
  // by qty when it reads the snapshot.
  const rtuLevelAmounts = rtuLineAmounts(commission, rates.rtuCeilingPct, rates.rtuLevelShares, 1);
  return { lineTotal, commission, gross, net, pv, rtuLevelAmounts };
}
